import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils.js'
import { Button } from './button.jsx'

/**
 * Modal dialog (shadcn/ui shape over Radix, D4).
 *
 * Radix rather than a hand-rolled overlay for the reasons D4 gives: focus trap,
 * focus restore on close, Escape handling, `aria-modal`, scroll lock and
 * outside-click are each individually easy to get wrong and collectively the
 * difference between a dialog that works with a keyboard and one that strands
 * the user. This product is used one-handed on a job site; a modal you cannot
 * dismiss is not a cosmetic problem.
 *
 * On phones the panel is bottom-anchored and full-width — a centred desktop
 * modal on a 375px screen puts its actions under the thumb-shadow at best and
 * off-screen at worst.
 */

export const Dialog = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogClose = DialogPrimitive.Close

export const DialogContent = React.forwardRef(function DialogContent (
  { className, children, title, description, ...props }, ref
) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(
          'fixed inset-0 z-50 bg-ink/40 backdrop-blur-[1px]',
          'data-[state=open]:animate-fade-in'
        )}
      />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed z-50 flex flex-col bg-surface shadow-overlay',
          // Phone: a bottom sheet that cannot exceed the viewport.
          'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-lg',
          // Desktop: a centred panel.
          'sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:max-w-lg',
          'sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg',
          'data-[state=open]:animate-slide-up',
          className
        )}
        {...props}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <DialogPrimitive.Title className="text-base font-semibold text-ink">
              {title}
            </DialogPrimitive.Title>
            {/* Radix warns when a dialog has no description; rendering it
                visually hidden when unused keeps screen readers informed and
                the console quiet. */}
            <DialogPrimitive.Description
              className={cn('mt-1 text-sm text-ink-muted', !description && 'sr-only')}
            >
              {description ?? title}
            </DialogPrimitive.Description>
          </div>
          <DialogPrimitive.Close asChild>
            <Button variant="ghost" size="icon-sm" aria-label="Close">
              <X />
            </Button>
          </DialogPrimitive.Close>
        </div>

        {/* The body scrolls, not the panel: the header stays legible and the
            footer's actions stay reachable on a long form. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
})

export function DialogFooter ({ className, ...props }) {
  return (
    <div
      className={cn(
        'flex flex-col-reverse gap-2 border-t border-line px-5 py-4 sm:flex-row sm:justify-end',
        // Safe-area padding so the primary action is not under the iOS home
        // indicator on a bottom sheet.
        'pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-4',
        className
      )}
      {...props}
    />
  )
}
